/** @jsxImportSource @emotion/react */
import { useState } from "react";
import { ButtonAction, ButtonPrimary } from "../../../components/NewButtons";
import * as authSelectors from "../../auth/authSelectors";
import { ConfirmActionModal } from "../../../components/ConfirmActionModal";
import { ActionSpinner } from "../../../components/ActionSpinner";
import { toastr } from "react-redux-toastr";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { StyledInfoBox } from "../../../components/StyledInfoBox";
import { css } from "@emotion/react";
import { useSelector } from "react-redux";
import {
  useGetMenuSalesChannels,
  usePublishMenuDraft,
  usePushMenuToSalesChannels,
} from "../service/menus-api";
import { SmallBadge } from "../../../components/SmallBadge";
import { StyledText } from "../../../assets/typography/typography";
import { colors } from "../../../components/colors";
import { SpinnerBlock } from "../../common/Spinner";
import { getErrorMessage, useHandleFetchError } from "../../../error-handler";

type Props = {
  menuId: string | undefined;
  isMenuPage?: boolean;
  savePublishAndPush?: () => Promise<void>;
  savePublishAndPushPublishIsLoading?: boolean;
};

const badgeAdditionalStyles = css`
  display: inline-block;
  width: fit-content;
  margin-right: 4px;
  margin-bottom: 4px;
`;

export const PublishAndPushMenuSelector = ({
  menuId,
  isMenuPage = false,
  savePublishAndPush,
  savePublishAndPushPublishIsLoading,
}: Props) => {
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const {
    data: menuSalesChannels,
    isLoading: salesChannelsIsLoading,
    error: getMenuSalesChannelsError,
  } = useGetMenuSalesChannels(organizationKeyName, menuId);

  const {
    mutateAsync: publishMenuDraft,
    isLoading: submitIsLoading,
  } = usePublishMenuDraft(menuId);

  const {
    mutateAsync: pushMenuToSalesChannels,
    isLoading: pushIsLoading,
  } = usePushMenuToSalesChannels(menuId);

  const onPublish = async () => {
    if (!menuId) {
      return;
    }
    try {
      // Publishing menu (From draft -> Active)
      await publishMenuDraft({
        id: menuId,
        organizationKeyName,
      });

      // Pushing externally right away
      await pushMenuToSalesChannels({
        menuId,
        organizationKeyName,
      });
      setModalOpen(false);
      toastr.success("Success", "Menu succesfully pushed");
    } catch (error: unknown) {
      const message = getErrorMessage(error);
      toastr.error("Error", message);
    }
  };

  const actionOnClick = async () => {
    if (savePublishAndPush) {
      await savePublishAndPush();
    } else {
      await onPublish();
    }
  };

  useHandleFetchError(getMenuSalesChannelsError);

  return (
    <>
      {isMenuPage ? (
        <ButtonAction
          onClick={() => setModalOpen(true)}
          type="button"
          icon={<FontAwesomeIcon icon={solid("upload")} />}
        >
          Save and push edits
        </ButtonAction>
      ) : (
        <ButtonPrimary onClick={() => setModalOpen(true)} buttonSize="small">
          Push edits
        </ButtonPrimary>
      )}
      <ConfirmActionModal
        onClose={() => setModalOpen(false)}
        isOpen={modalOpen}
        width={600}
        title="Push menu edits to sales channels"
        subtitle="Are you sure you want to continue with pushing menu edits?"
        confirmButton={
          <ButtonAction onClick={actionOnClick} type="button">
            {submitIsLoading ||
            savePublishAndPushPublishIsLoading ||
            pushIsLoading ? (
              <ActionSpinner />
            ) : (
              "Yes, push edits"
            )}
          </ButtonAction>
        }
      >
        {salesChannelsIsLoading ? (
          <SpinnerBlock />
        ) : (
          <>
            {menuSalesChannels?.length === 0 && (
              <StyledInfoBox
                title="OBS: Remember to add this menu til sales channels"
                subtitle="No sales channels are currently using this menu."
              />
            )}
            {menuSalesChannels && menuSalesChannels.length > 0 && (
              <StyledInfoBox
                title="OBS: Changes can take 10-15 min to take effect"
                subtitle="It can take up to 15 min before you can see your changes live in HeapsGo and the following external sales channels:"
              >
                {menuSalesChannels.map((res, i) => (
                  <SmallBadge
                    key={i}
                    backgroundColor={colors.greyBorder}
                    additionalStyles={badgeAdditionalStyles}
                  >
                    <StyledText className="bodySubtextRegular mb-0">
                      {res.name}
                    </StyledText>
                  </SmallBadge>
                ))}
              </StyledInfoBox>
            )}
          </>
        )}
      </ConfirmActionModal>
    </>
  );
};
